import React, { useEffect } from 'react';
import { constants } from '@clubmed/utils';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { BE_TRACKING, TRACK_EVENT_NAMES } from 'Tracking/constants';
import { getPriceDiff, getResolvedProposals, getTotalValue } from 'Utils/proposal/proposalUtils';
import { useAppContext } from 'Context/ReactAppContext';
import { selectProposal as selectProposalType } from 'Store/booking/selected-proposal-id/selectedProposalId.action';
import { t } from 'Src/i18n';
import * as trackingStorageApi from 'Tracking/tracking-storage-api';
import { BookingAccommodations } from 'Components/widgets/accommodations/BookingAccommodations';
import { Tabs } from 'Components/frameworks/tabs/Tabs';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { ProposalAccommodationTitle } from './proposal-accommodation-title/ProposalAccommodationTitle';
import { AccommodationsComparator } from './AccommodationsComparator';
import { getUniqDiscountProposals } from './proposal-accommodation-title/ProposalAccommodationTitleUtils';
const { FLEX_RATES } = constants;
type Props = {
  isComparatorView?: boolean;
  isLoading: boolean;
  product: Product;
  proposals: Array<Proposal>;
  selectedProposal: Proposal;
  selectedRate: string;
  selectProposal: typeof selectProposalType;
  setSelectedRate: (arg0: string) => void;
  streetViews?: Array<StreetViewWithAccommodationCategoryId>;
  switchOffers: (offers: Array<string>) => void;
  tabTheme?: string;
};

const AccommodationsTabs = ({
  isComparatorView,
  isLoading,
  product,
  proposals,
  selectedProposal,
  selectedRate,
  selectProposal,
  setSelectedRate,
  streetViews,
  switchOffers,
  tabTheme,
}: Props) => {
  const appContext = useAppContext();
  const { locale } = appContext;
  const { trackClickEvent } = useTrackEvent();
  const uniqDiscounts = getUniqDiscountProposals(proposals);

  const updateUpsellRevenue = () => {
    const upsellRevenue =
      selectedProposal.price.total.value -
      Math.min(
        ...proposals.map(
          ({
            price: {
              total: { value },
            },
          }) => value,
        ),
      );
    let rateType = '';

    switch (selectedRate) {
      case FLEX_RATES.STANDARD:
        rateType = 'standard';
        break;

      case FLEX_RATES.FLR:
        rateType = 'FlexibleRate';
        break;

      default:
        rateType = 'nonRefundableRate';
        break;
    }

    /*
      We need this information on BookingConfirmationView.
      To handle external payment provider redirection we have to store it outside of redux
     */
    trackingStorageApi.set({ ...trackingStorageApi.get(BE_TRACKING), rateType, upsellRevenue });
  };

  useEffect(() => {
    updateUpsellRevenue();
  }, [selectedProposal]);

  const onProposalSelection = (itemIndex: number): void => {
    const proposal = proposals[itemIndex];

    if (proposal?.id && !proposal.errorCode) {
      trackClickEvent(TRACK_EVENT_NAMES.CLICK_ACCOMMODATION, {
        detail_click: 'change_comfort',
        room_type: proposal.accommodations[0].comfortId?.split('_')?.pop(),
      });
      selectProposal(proposal.id);
      setSelectedRate(proposal.price.selectedRate);
    }
  };

  const initialSelectedItem = Math.max(0, proposals.indexOf(selectedProposal));
  const selectedProposalPrice = getTotalValue(selectedProposal);
  const proposalsLength = proposals.length;
  const resolvedProposals = getResolvedProposals(proposals);
  const formatedPrices = resolvedProposals.map((proposal) => {
    return getPriceDiff(
      getTotalValue(proposal),
      selectedProposalPrice,
      locale,
      selectedProposal.price.currency,
    );
  });
  const formattedPricesPerNight = resolvedProposals.map((proposal) => {
    return getPriceDiff(
      getTotalValue(proposal) / proposal.duration,
      selectedProposalPrice / proposal.duration,
      locale,
      selectedProposal.price.currency,
    );
  });
  const displayPrice = isFeatureFlipped(appContext)('booking.pricePerNight')
    ? formattedPricesPerNight
    : formatedPrices;

  const getPriceLabel = (formatedPrice) => {
    let priceLabel = '';

    if (isFeatureFlipped(appContext)('booking.pricePerNight')) {
      priceLabel = ` ${t('booking.accommodation.moreRooms.perNight')}`;
    }

    if (formatedPrice === null) {
      priceLabel = t('booking.accommodation.packageContent.included');
    }

    return priceLabel;
  };

  const items = resolvedProposals.map((proposal, index) => {
    const formatedPrice = displayPrice[index];
    return {
      header: {
        title: (
          <ProposalAccommodationTitle
            includedLabel={getPriceLabel(displayPrice[index])}
            numberOfTabs={proposalsLength}
            priceLabel={formatedPrice}
            product={product}
            proposal={proposal}
            theme={tabTheme}
            uniqDiscounts={uniqDiscounts}
          />
        ),
      },
      content: [
        <BookingAccommodations
          accommodations={proposal.accommodations}
          isLoading={isLoading}
          key={proposal.id}
          product={product}
          proposal={proposal}
          selectedRate={selectedRate}
          setSelectedRate={setSelectedRate}
          streetViews={streetViews}
          switchOffers={switchOffers}
          theme={isComparatorView ? 'default' : 'large'}
          accommodationsComparator={
            <AccommodationsComparator
              activeIndexes={index}
              formatedPrices={formatedPrices}
              product={product}
              proposals={proposals}
              resolvedProposals={resolvedProposals}
            />
          }
        />,
      ],
    };
  });
  return (
    <Tabs
      initialSelectedItem={initialSelectedItem}
      isContainerWide
      items={items}
      onSelect={onProposalSelection}
      theme="rounded"
    />
  );
};

export { AccommodationsTabs };
